import { Fragment, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, office, till, upload } from "@/lib/api";
import { dateTime, money, personName, time, toMinor, fromMinor } from "@/lib/format";
import { useTill } from "@/lib/till-context";
import { Badge, Button, Field, Input, Modal, Pager, Select, cx, useConfirm, usePaged, useToast } from "@/components/ui";
import { Link, useSearchParams } from "react-router";
import { Card, PageHead, td, th } from "@/components/Layout";
import { Icon } from "@/components/Icon";
import { useUnits } from "@/components/SupplierBits";
import { Suppliers } from "./Suppliers";
import { TillRoles } from "./Staff";
import { YourTillRoles } from "@/components/TillRoleBits";

type Shop = { id: string; name: string };

/** The store's shops, and which is picked. No shops: everything is the one "main" shop. */
function useShopPick() {
  const q = useQuery({ queryKey: ["shops"], queryFn: () => api<{ shops: Shop[] }>(till("/admin/shops")) });
  const shops = q.data?.shops ?? [];
  const [picked, setPicked] = useState("");
  const shopId = picked || shops[0]?.id || "main";
  const select = shops.length ? (
    <Select aria-label="Shop" value={shopId} onChange={(e) => setPicked(e.target.value)} className="!w-52">
      {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
    </Select>
  ) : null;
  return { shops, shopId, select };
}

const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

// ─────────────────────────── categories and brands ───────────────────────────
type Names = { categories: { name: string; count: number }[]; brands: { name: string; count: number }[]; noCategory: number };

export function Catalog() {
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { shopId, select } = useShopPick();
  const [sp] = useSearchParams();
  const [tab, setTab] = useState<"category" | "brand" | "unit" | "supplier">(sp.get("tab") === "suppliers" ? "supplier" : "category");
  const kind = tab === "brand" ? "brand" : "category";
  const brandPage = !!import.meta.env.VITE_BRAND;
  const [name, setName] = useState("");
  const [edit, setEdit] = useState<{ from: string; to: string; merge: string } | null>(null);
  const q = useQuery({ queryKey: ["catalog-names", shopId], queryFn: () => api<Names>(till(`/admin/catalog-names?shop=${encodeURIComponent(shopId)}`)) });
  const list = (kind === "category" ? q.data?.categories : q.data?.brands) ?? [];
  const listPage = usePaged(list);
  const refresh = () => {
    void q.refetch();
    void qc.invalidateQueries({ queryKey: ["admin-items"] });
  };
  const add = async () => {
    try {
      await api(till("/admin/catalog-names"), { body: { kind, name, shopId } });
      setName("");
      refresh();
    } catch (e) {
      toast(errText(e, "Couldn't add it"), "err");
    }
  };
  const save = async (to: string) => {
    if (!edit) return;
    try {
      await api(till("/admin/catalog-names"), { method: "PUT", body: { kind, from: edit.from, to, shopId } });
      setEdit(null);
      refresh();
      toast("Saved. Products follow the new name");
    } catch (e) {
      toast(errText(e, "Couldn't save"), "err");
    }
  };
  const remove = async (n: string) => {
    if (!(await confirm({ title: `Delete ${n}?`, ok: "Delete", danger: true }))) return;
    try {
      await api(till("/admin/catalog-names/delete"), { body: { kind, name: n, shopId } });
      refresh();
    } catch (e) {
      toast(errText(e, "Couldn't delete it"), "err");
    }
  };
  return (
    <>
      <PageHead title={brandPage ? "Categories, brands & units" : "Categories & brands"} sub="The lists your team picks from when adding a product.">{tab === "unit" || tab === "supplier" ? null : select}</PageHead>
      <div className="mb-3 flex flex-wrap gap-2">
        <Button tone={tab === "category" ? "dark" : "light"} onClick={() => setTab("category")}>Categories <span className="font-mono">{q.data?.categories.length ?? 0}</span></Button>
        <Button tone={tab === "brand" ? "dark" : "light"} onClick={() => setTab("brand")}>Brands <span className="font-mono">{q.data?.brands.length ?? 0}</span></Button>
        {brandPage ? <Button tone={tab === "unit" ? "dark" : "light"} onClick={() => setTab("unit")}>Sold by</Button> : null}
      </div>
      {tab === "supplier" ? <Suppliers embedded /> : tab === "unit" ? <UnitsPanel /> : <>
      {kind === "category" && q.data?.noCategory ? <p className="mb-3 border border-warn bg-warn-tint px-4 py-3 text-sm font-semibold text-warn-ink">{q.data.noCategory} product{q.data.noCategory === 1 ? " has" : "s have"} no category yet.</p> : null}
      <Card>
        <div className="flex flex-wrap gap-2 p-4">
          <Input aria-label={`New ${kind}`} value={name} onChange={(e) => setName(e.target.value)} placeholder={kind === "category" ? "New category name" : "New brand name"} className="min-w-[220px] flex-1" />
          <Button tone="primary" onClick={() => void add()} disabled={!name.trim()}>Add {kind}</Button>
        </div>
        {listPage.rows.map((n) => (
          <div key={n.name} className="flex flex-wrap items-center gap-3 border-t border-slate-100 px-4 py-2.5 text-sm">
            <b className="min-w-0 flex-1 font-semibold">{n.name}</b>
            <span className="text-slate-500">{n.count} product{n.count === 1 ? "" : "s"}</span>
            <Button size="sm" onClick={() => setEdit({ from: n.name, to: n.name, merge: "" })}>Edit</Button>
            {n.count === 0 ? <Button size="sm" tone="danger" onClick={() => void remove(n.name)}>Delete</Button> : null}
          </div>
        ))}
        {!list.length ? <p className="border-t border-slate-100 px-4 py-8 text-center text-sm text-slate-500">Nothing here yet.</p> : null}
        <Pager page={listPage.page} size={listPage.size} total={listPage.total} onPage={listPage.setPage} />
      </Card>
      </>}
      {edit ? (
        <Modal title={`Edit ${edit.from}`} onClose={() => setEdit(null)} footer={<Button onClick={() => setEdit(null)}>Close</Button>}>
          <div className="flex flex-col gap-3">
            <Field label="Name"><Input value={edit.to} onChange={(e) => setEdit({ ...edit, to: e.target.value })} /></Field>
            <Button tone="primary" onClick={() => void save(edit.to)} disabled={!edit.to.trim()}>Save name</Button>
            <Field label="Merge into" hint="Moves its products across and removes this name">
              <Select value={edit.merge} onChange={(e) => setEdit({ ...edit, merge: e.target.value })}>
                <option value="">Choose one</option>
                {list.filter((x) => x.name !== edit.from).map((x) => <option key={x.name} value={x.name}>{x.name}</option>)}
              </Select>
            </Field>
            <Button onClick={() => void save(edit.merge)} disabled={!edit.merge}>Merge</Button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

/** The Sold by list: one list for all shops. */
function UnitsPanel() {
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const q = useUnits();
  const list = q.data?.units ?? [];
  const page = usePaged(list);
  const [name, setName] = useState("");
  const [edit, setEdit] = useState<{ from: string; to: string; merge: string } | null>(null);
  const refresh = () => {
    void q.refetch();
    void qc.invalidateQueries({ queryKey: ["admin-items"] });
  };
  const add = async () => {
    try {
      await api(till("/admin/units"), { body: { name } });
      setName("");
      refresh();
    } catch (e) {
      toast(errText(e, "Couldn't add it"), "err");
    }
  };
  const save = async (to: string) => {
    if (!edit) return;
    try {
      await api(till("/admin/units"), { method: "PUT", body: { from: edit.from, to } });
      setEdit(null);
      refresh();
      toast("Saved. Products follow the new name");
    } catch (e) {
      toast(errText(e, "Couldn't save"), "err");
    }
  };
  const remove = async (n: string) => {
    if (!(await confirm({ title: `Delete ${n}?`, ok: "Delete", danger: true }))) return;
    try {
      await api(till("/admin/units/delete"), { body: { name: n } });
      refresh();
    } catch (e) {
      toast(errText(e, "Couldn't delete it"), "err");
    }
  };
  return (
    <>
      <Card>
        <p className="px-4 pt-4 text-[13px] text-slate-500">What products are sold by. These apply to all shops.</p>
        <div className="flex flex-wrap gap-2 p-4">
          <Input aria-label="New unit" value={name} maxLength={30} onChange={(e) => setName(e.target.value)} placeholder="New unit name, e.g. drum" className="min-w-[220px] flex-1" />
          <Button tone="primary" onClick={() => void add()} disabled={!name.trim()}>Add unit</Button>
        </div>
        {page.rows.map((n) => (
          <div key={n.name} className="flex flex-wrap items-center gap-3 border-t border-slate-100 px-4 py-2.5 text-sm">
            <b className="min-w-0 flex-1 font-semibold capitalize">{n.name}</b>
            <span className="text-slate-500">{n.count} product{n.count === 1 ? "" : "s"}</span>
            <Button size="sm" onClick={() => setEdit({ from: n.name, to: n.name, merge: "" })}>Edit</Button>
            {n.count === 0 ? <Button size="sm" tone="danger" onClick={() => void remove(n.name)}>Delete</Button> : null}
          </div>
        ))}
        <Pager page={page.page} size={page.size} total={page.total} onPage={page.setPage} />
      </Card>
      {edit ? (
        <Modal title={`Edit ${edit.from}`} onClose={() => setEdit(null)} footer={<Button onClick={() => setEdit(null)}>Close</Button>}>
          <div className="flex flex-col gap-3">
            <Field label="Name"><Input value={edit.to} onChange={(e) => setEdit({ ...edit, to: e.target.value })} /></Field>
            <Button tone="primary" onClick={() => void save(edit.to)} disabled={!edit.to.trim()}>Save name</Button>
            <Field label="Merge into" hint="Moves its products across and removes this name">
              <Select value={edit.merge} onChange={(e) => setEdit({ ...edit, merge: e.target.value })}>
                <option value="">Choose one</option>
                {list.filter((x) => x.name !== edit.from).map((x) => <option key={x.name} value={x.name}>{x.name}</option>)}
              </Select>
            </Field>
            <Button onClick={() => void save(edit.merge)} disabled={!edit.merge}>Merge</Button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

// ─────────────────────────── discounts ───────────────────────────
type Deal = { id: string; shopId: string; kind: string; name: string; scope: string; percent: number; wholesale: boolean; active: boolean; endsAt: number | null };

export function Discounts() {
  const toast = useToast();
  const { shops, shopId, select } = useShopPick();
  const q = useQuery({ queryKey: ["deals"], queryFn: () => api<{ deals: Deal[] }>(till("/admin/deals")) });
  const names = useQuery({ queryKey: ["catalog-names", shopId], queryFn: () => api<Names>(till(`/admin/catalog-names?shop=${encodeURIComponent(shopId)}`)) });
  const items = useQuery({ queryKey: ["admin-items"], queryFn: () => api<{ items: { id: string; name: string; location?: string; active: boolean }[] }>(till("/admin/items")) });
  const blank = { kind: "Flash sale", name: "", scope: "", percent: "", endsAt: "", wholesale: false };
  const [f, setF] = useState<typeof blank | null>(null);
  const deals = (q.data?.deals ?? []).filter((d) => d.shopId === shopId);
  const dealPage = usePaged(deals);
  const save = async () => {
    if (!f) return;
    try {
      const r = await api<{ belowCost: number }>(till("/admin/deals"), { body: { ...f, percent: Number(f.percent), shopId } });
      toast(r.belowCost ? `Started. ${r.belowCost} product${r.belowCost === 1 ? " is" : "s are"} now below cost` : "Started. Till prices change by themselves", r.belowCost ? "info" : "ok");
      setF(null);
      void q.refetch();
    } catch (e) {
      toast(errText(e, "Couldn't save"), "err");
    }
  };
  const toggle = async (d: Deal) => {
    await api(till(`/admin/deals/${d.id}`), { method: "PUT", body: { active: !d.active } });
    void q.refetch();
  };
  const where = (s: string) => {
    if (!s) return "Everything in the shop";
    if (s.startsWith("c:")) return s.slice(2);
    return items.data?.items.find((i) => i.id === s.slice(2))?.name ?? "One product";
  };
  return (
    <>
      <PageHead title="Discounts" sub="Flash sales, discounts and clearance. Set them here and they apply at the till by themselves.">
        {select}
        <Button tone="primary" onClick={() => setF(blank)}>New discount</Button>
      </PageHead>
      <Card>
        {dealPage.rows.map((d) => (
          <div key={d.id} className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-4 py-3 text-sm">
            <Badge tone={d.kind === "Clearance" ? "dark" : "brand"}>{d.kind}</Badge>
            <span className="min-w-[220px] flex-1"><b className="block">{d.name} · {d.percent}% off</b><span className="text-xs text-slate-500">{where(d.scope)}{d.endsAt ? ` · ends ${dateTime(d.endsAt)}` : " · no end date"} · {d.wholesale ? "wholesale buyers get it too" : "not for wholesale buyers"}</span></span>
            <b className={cx("text-[13px]", d.active ? "text-brand" : "text-slate-500")}>{d.active ? "Running" : "Off"}</b>
            <Button size="sm" onClick={() => void toggle(d)}>{d.active ? "Turn off" : "Turn on"}</Button>
          </div>
        ))}
        {!deals.length ? <p className="px-4 py-8 text-center text-sm text-slate-500">No discounts yet.</p> : null}
        <Pager page={dealPage.page} size={dealPage.size} total={dealPage.total} onPage={dealPage.setPage} />
      </Card>
      {f ? (
        <Modal wide title="New discount" sub={shops.find((s) => s.id === shopId)?.name} onClose={() => setF(null)} footer={<><Button onClick={() => setF(null)}>Cancel</Button><Button tone="primary" onClick={() => void save()}>Start discount</Button></>}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Kind"><Select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}><option>Flash sale</option><option>Discount</option><option>Clearance</option></Select></Field>
            <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Weekend cement sale" /></Field>
            <Field label="Applies to">
              <Select value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })}>
                <option value="">Everything in this shop (general)</option>
                {(names.data?.categories ?? []).map((c) => <option key={c.name} value={`c:${c.name}`}>Category: {c.name}</option>)}
                {(items.data?.items ?? []).filter((i) => (i.location ?? "main") === shopId && i.active).map((i) => <option key={i.id} value={`p:${i.id}`}>Product: {i.name}</option>)}
              </Select>
            </Field>
            <Field label="Percent off"><Input inputMode="numeric" value={f.percent} onChange={(e) => setF({ ...f, percent: e.target.value.replace(/\D/g, "") })} placeholder="e.g. 10" /></Field>
            <Field label="Ends (optional)"><Input type="date" value={f.endsAt} onChange={(e) => setF({ ...f, endsAt: e.target.value })} /></Field>
            <label className="flex items-center gap-2 self-end pb-3 text-sm font-semibold"><input type="checkbox" checked={f.wholesale} onChange={(e) => setF({ ...f, wholesale: e.target.checked })} className="h-5 w-5 accent-[var(--brand)]" /> Wholesale buyers get this too</label>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

// ─────────────────────────── cash control ───────────────────────────
const DEST: Record<string, string> = { bank: "Bank", momo: "Mobile Money", owner: "Owner", other: "Other" };
const needsRef = (dest?: string | null) => !dest || dest === "bank" || dest === "momo";
type Cash = {
  me: string;
  can: { receive: boolean; deposit: boolean; confirm: boolean };
  waiting: { id: string; shop?: string | null; teller: string; till: string; closedAt: number | null }[];
  safeByShop?: Record<string, number>;
  inSafe: number;
  oldestAt: number | null;
  lastBankAt: number | null;
  deposits: { destination?: string; account?: string | null; id: string; slipPhoto: string | null; amount: number; slip: string | null; reason: string | null; by: string; taker: string | null; at: number; confirmedBy: string | null; history: string[] }[];
  handovers?: { id: string; shop?: string | null; teller: string; countedBy: string | null; counted: number; difference: number; reason: string | null; closedAt: number | null }[];
  safe: { id: string; shop?: string | null; type: string; what: string; by: string; taker: string | null; at: number; amount: number; balance: number }[];
};
/** Email addresses inside a sentence, shown as names. */
const namesIn = (text: string) => text.replace(/[^\s,]+@[^\s,]+/g, (e) => personName(e));
const DAY = 24 * 3600 * 1000;

export function CashControl() {
  const toast = useToast();
  const { me } = useTill();
  const { shops } = useShopPick();
  // Everything across all shops by default, or one shop. Someone limited to one shop only has that one.
  const canSeeAll = shops.length > 1;
  const [picked, setPicked] = useState("all");
  const shopId = shops.length <= 1 ? shops[0]?.id ?? "main" : picked;
  const allShops = shopId === "all";
  const select = canSeeAll ? (
    <Select aria-label="Shop" value={shopId} onChange={(e) => setPicked(e.target.value)} className="!w-52">
      <option value="all">All shops</option>
      {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
    </Select>
  ) : null;
  const [targetShop, setTargetShop] = useState("");
  const actionShop = allShops ? targetShop : shopId;
  const whereFrom = (shop?: string | null) => (allShops && shop ? `${shop} · ` : "");
  const [tab, setTab] = useState<"todo" | "deposits" | "safe">("todo");
  const q = useQuery({ queryKey: ["cash", shopId], queryFn: () => api<Cash>(till(`/admin/cash?shop=${encodeURIComponent(shopId)}`)) });
  const d = q.data;
  const [recv, setRecv] = useState<{ id: string; label: string; amount: string; note: string; mismatch: string } | null>(null);
  const [dep, setDep] = useState<{ noRef?: boolean; destination: string; account: string; amount: string; taker: string; slip: string; reason: string; slipPhoto: string } | null>(null);
  const accountsQ = useQuery({ queryKey: ["cash-accounts"], queryFn: () => api<{ accounts: { name: string; kind: string }[] }>(till("/admin/cash/accounts")) });
  const uploadSlip = async (file: File | undefined) => {
    if (!file || !dep) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await upload<{ url: string }>(till("/admin/cash/slip-photo"), fd);
      setDep({ ...dep, slipPhoto: r.url });
      toast("Slip photo added");
    } catch (e) {
      toast(errText(e, "The photo didn't upload"), "err");
    }
  };
  const [act, setAct] = useState<{ id: string; action: "slip" | "correct" | "reopen"; value: string; reason: string; hasSlip: boolean } | null>(null);
  const [float, setFloat] = useState<{ amount: string; till: string } | null>(null);
  void me;
  const stale = !!d && d.inSafe > 0 && d.oldestAt != null && Date.now() - Math.max(d.oldestAt, d.lastBankAt ?? 0) > DAY;
  const toConfirm = (d?.deposits ?? []).filter((x) => !x.confirmedBy);
  const receive = async () => {
    if (!recv) return;
    try {
      const r = await api<{ matched: boolean }>(till("/admin/cash/receive"), { body: { shiftId: recv.id, counted: toMinor(recv.amount), note: recv.note } });
      toast(r.matched ? "Counts match. Cash is in the safe" : "Saved and flagged for review", r.matched ? "ok" : "info");
      setRecv(null);
      void q.refetch();
    } catch (e) {
      setRecv({ ...recv, mismatch: errText(e, "Couldn't save") });
    }
  };
  const deposit = async () => {
    if (!dep) return;
    try {
      await api(till("/admin/cash/deposit"), { body: { destination: dep.destination, account: dep.account, amount: toMinor(dep.amount), taker: dep.taker, slip: needsRef(dep.destination) ? dep.slip : "", slipPhoto: needsRef(dep.destination) ? dep.slipPhoto : "", reason: dep.destination === "owner" || dep.slip ? "" : dep.reason, shopId: actionShop } });
      toast("Transfer saved");
      setDep(null);
      setTab("todo");
      void q.refetch();
    } catch (e) {
      toast(errText(e, "Couldn't save"), "err");
    }
  };
  const run = async (id: string, body: Record<string, unknown>) => {
    try {
      await api(till(`/admin/cash/deposit/${id}`), { body });
      setAct(null);
      void q.refetch();
      toast("Saved, and kept in the history");
    } catch (e) {
      toast(errText(e, "Couldn't save"), "err");
    }
  };
  const saveFloat = async () => {
    if (!float) return;
    try {
      await api(till("/admin/cash/float"), { body: { amount: toMinor(float.amount), till: float.till, shopId: actionShop } });
      setFloat(null);
      void q.refetch();
    } catch (e) {
      toast(errText(e, "Couldn't save"), "err");
    }
  };
  // Everything waiting on someone, in one list, in the order the cash moves.
  const confirmCount = async (id: string) => {
    try {
      await api(till(`/admin/cash/handover/${id}/confirm`), { body: {} });
      toast("Count confirmed");
      void q.refetch();
    } catch (e) {
      toast(errText(e, "Couldn't confirm it"), "err");
    }
  };
  type Todo = { id: string; title: string; line: string; note?: string; button?: { label: string; go: () => void } };
  const todos: Todo[] = [
    ...(d?.waiting ?? []).map((w): Todo => ({ id: `w${w.id}`, title: `Count ${personName(w.teller)}'s cash`, line: `${whereFrom(w.shop)}${w.till}${w.closedAt ? ` · shift closed ${dateTime(w.closedAt)}` : ""}`, button: d?.can.receive ? { label: "Count cash", go: () => setRecv({ id: w.id, label: `${personName(w.teller)}, ${w.till}`, amount: "", note: "", mismatch: "" }) } : undefined })),
    ...(d?.handovers ?? []).map((h): Todo => ({ id: `h${h.id}`, title: `Check ${personName(h.countedBy)}'s count of ${personName(h.teller)}'s cash`, line: `${whereFrom(h.shop)}${money(h.counted)} counted · ${h.difference === 0 ? "exact" : `${h.difference < 0 ? "short" : "over"} ${money(Math.abs(h.difference))}`}${h.reason ? ` · ${h.reason}` : ""}`, button: d?.can.receive ? { label: "Confirm", go: () => void confirmCount(h.id) } : undefined })),
    ...toConfirm.filter((x) => !x.slip && needsRef(x.destination)).map((x): Todo => ({ id: `s${x.id}`, title: `Add the reference for ${money(x.amount)}`, line: `${whereFrom((x as { shop?: string | null }).shop)}${DEST[x.destination ?? "bank"]}${x.account ? ` (${x.account})` : ""} · taken by ${personName(x.taker)} · ${dateTime(x.at)}`, note: Date.now() - x.at > DAY ? "Over 24 hours" : undefined, button: d?.can.deposit ? { label: "Add reference", go: () => setAct({ id: x.id, action: "slip", value: "", reason: "", hasSlip: false }) } : undefined })),
    ...toConfirm.filter((x) => x.slip || !needsRef(x.destination)).map((x): Todo => ({ id: `c${x.id}`, title: `Check ${money(x.amount)} arrived`, line: `${whereFrom((x as { shop?: string | null }).shop)}${DEST[x.destination ?? "bank"]}${x.account ? ` (${x.account})` : ""}${x.slip ? ` · reference ${x.slip}` : ""} · recorded by ${personName(x.by)} · ${dateTime(x.at)}`, note: x.by === d?.me ? "Someone else must confirm" : undefined, button: d?.can.confirm && x.by !== d?.me ? { label: "It arrived", go: () => void run(x.id, { action: "confirm" }) } : undefined })),
  ];
  const todoPage = usePaged(todos);
  const safePage = usePaged(d?.safe ?? []);
  const depositPage = usePaged(d?.deposits ?? []);
  const tabs = [["todo", "To do", todos.length], ["deposits", "Transfers", 0], ["safe", "Safe history", 0]] as const;
  let lastDay = "";
  return (
    <>
      <PageHead title="Cash control" sub="Count each shift's cash, keep it in the safe, then transfer it.">{select}</PageHead>
      <section className="ui-hero mb-4 flex flex-wrap items-center justify-between gap-4 border border-slate-200 bg-white p-4">
        <span className="min-w-[220px]">
          <span className="text-xs font-semibold text-slate-500">{allShops ? "Cash in the safes" : "Cash in the safe"}</span>
          <b className="mt-0.5 block text-[28px] font-extrabold leading-tight tracking-tight">{money(d?.inSafe ?? 0)}</b>
          {stale ? <span className="mt-0.5 block text-[13px] font-semibold text-warn-ink">Some has been there over 24 hours. Transfer it today.</span> : <span className="mt-0.5 block text-xs text-slate-500">{d?.inSafe ? "Transferred on time" : "Nothing waiting to be transferred"}</span>}
        </span>
        {d?.can.deposit ? <span className="flex flex-wrap gap-2"><Button onClick={() => setFloat({ amount: "", till: "" })}>Take starting cash</Button><Button tone="primary" onClick={() => setDep({ destination: "bank", account: "", amount: "", taker: "", slip: "", reason: "Slip book finished", slipPhoto: "" })}>Transfer cash</Button></span> : null}
      </section>
      <Card>
        <div role="tablist" aria-label="Cash control" className="no-scrollbar flex overflow-x-auto border-b border-slate-200 px-3 pt-1.5">
          {tabs.map(([k, l, n]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={cx("flex h-11 items-center gap-2 whitespace-nowrap !rounded-none border-b-2 px-3.5 text-sm", tab === k ? "border-brand font-bold" : "border-transparent text-slate-500")}>
              {l}{n ? <span className="ui-pill bg-brand px-2 text-[11px] font-extrabold text-white">{n}</span> : null}
            </button>
          ))}
        </div>
        {tab === "todo" ? (
          <>
            {todoPage.rows.map((x) => (
              <div key={x.id} className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-3 text-sm">
                <span className="min-w-[260px] flex-1"><b className="block font-semibold">{x.title}</b><span className="text-xs text-slate-500">{x.line}</span></span>
                {x.note ? <span className="text-xs text-slate-500">{x.note}</span> : null}
                {x.button ? <Button size="sm" tone="primary" onClick={x.button.go}>{x.button.label}</Button> : null}
              </div>
            ))}
            {d && !todos.length ? <p className="px-5 py-10 text-center text-sm text-slate-500">All done. Nothing needs you right now.</p> : null}
            <Pager page={todoPage.page} size={todoPage.size} total={todoPage.total} onPage={todoPage.setPage} />
          </>
        ) : tab === "deposits" ? (
          <>
            {depositPage.rows.map((x) => (
              <div key={x.id} className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-3 text-sm">
                <span className="min-w-[260px] flex-1">
                  <span className="flex flex-wrap items-center gap-2"><b className="text-[15px] font-extrabold">{money(x.amount)}</b><Badge>{DEST[x.destination ?? "bank"]}</Badge>{x.confirmedBy ? <Badge tone="brand">Arrived</Badge> : x.slip || !needsRef(x.destination) ? <Badge tone="slate">Not checked yet</Badge> : <Badge tone="amber">No reference</Badge>}</span>
                  <span className="mt-0.5 block text-xs text-slate-500">{whereFrom((x as { shop?: string | null }).shop)}{x.account ? `${x.account} · ` : ""}{x.slip ? `Reference ${x.slip} · ` : ""}{dateTime(x.at)} · taken by {personName(x.taker)}{x.confirmedBy ? ` · checked by ${personName(x.confirmedBy)}` : ""}</span>
                  {x.slipPhoto ? <a href={x.slipPhoto} target="_blank" rel="noreferrer" className="block text-xs font-semibold text-brand underline">View slip photo</a> : null}
                  {!x.slip && x.reason ? <span className={cx("block text-xs", needsRef(x.destination) ? "text-warn-ink" : "text-slate-500")}>{needsRef(x.destination) ? "Reason" : "For"}: {x.reason}</span> : null}
                  {x.history.map((h, i) => <span key={i} className="block text-xs text-slate-500">{namesIn(h)}</span>)}
                </span>
                <span className="flex flex-wrap items-center justify-end gap-2">
                  {!x.confirmedBy && x.slip && d?.can.deposit ? <><Button size="sm" onClick={() => setAct({ id: x.id, action: "slip", value: x.slip ?? "", reason: "", hasSlip: true })}>Change reference</Button><Button size="sm" onClick={() => setAct({ id: x.id, action: "correct", value: "", reason: "", hasSlip: true })}>Correct amount</Button></> : null}
                  {x.confirmedBy ? <Button size="sm" onClick={() => setAct({ id: x.id, action: "reopen", value: "", reason: "", hasSlip: true })}>Undo</Button> : null}
                </span>
              </div>
            ))}
            {d && !d.deposits.length ? <p className="px-5 py-10 text-center text-sm text-slate-500">No cash transferred yet.</p> : null}
            <Pager page={depositPage.page} size={depositPage.size} total={depositPage.total} onPage={depositPage.setPage} />
          </>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead><tr>{["What happened", "Money", allShops ? "All safes after" : "Safe after"].map((h, i) => <th key={h} className={cx(th, i >= 1 && "text-right")}>{h}</th>)}</tr></thead>
                <tbody>
                  {safePage.rows.flatMap((l) => {
                    const day = new Date(l.at).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
                    const head = day !== lastDay ? <tr key={`d${l.id}`}><td colSpan={3} className="bg-slate-50 px-3.5 py-2 text-xs font-bold text-slate-600">{day}</td></tr> : null;
                    lastDay = day;
                    return [head, (
                      <tr key={l.id}>
                        <td className={cx(td, "whitespace-normal")}><b className="block font-semibold">{namesIn(l.what)}</b><span className="text-xs text-slate-500">{whereFrom(l.shop)}{time(l.at)} · {personName(l.by)}{l.taker ? ` · taken by ${personName(l.taker)}` : ""}</span></td>
                        <td className={cx(td, "text-right font-bold")}>{l.amount === 0 ? "No change" : `${l.amount < 0 ? "Out " : "In "}${money(Math.abs(l.amount))}`}</td>
                        <td className={cx(td, "text-right text-slate-500")}>{money(l.balance)}</td>
                      </tr>
                    )];
                  })}
                </tbody>
              </table>
            </div>
            {d && !d.safe.length ? <p className="px-5 py-10 text-center text-sm text-slate-500">Nothing in the safe history yet.</p> : null}
            <Pager page={safePage.page} size={safePage.size} total={safePage.total} onPage={safePage.setPage} />
          </>
        )}
      </Card>
      {recv ? (
        <Modal title="Count the cash" sub={`${recv.label}. Count it yourself. The teller's figure stays hidden until you save.`} onClose={() => setRecv(null)} footer={<><Button onClick={() => setRecv(null)}>Cancel</Button><Button tone="primary" onClick={() => void receive()}>Confirm and put in the safe</Button></>}>
          <div className="flex flex-col gap-3">
            <Field label="Amount you counted"><Input inputMode="decimal" value={recv.amount} onChange={(e) => setRecv({ ...recv, amount: e.target.value })} /></Field>
            {recv.mismatch ? <><p className="text-sm font-semibold text-warn-ink">{recv.mismatch}</p><Field label="Why is it different?"><Input value={recv.note} onChange={(e) => setRecv({ ...recv, note: e.target.value })} /></Field></> : null}
          </div>
        </Modal>
      ) : null}
      {dep ? (() => {
        const holds = allShops ? (targetShop ? d?.safeByShop?.[targetShop] ?? 0 : null) : d?.inSafe ?? 0;
        const amount = toMinor(dep.amount);
        const tooMuch = holds != null && amount > holds;
        const isOther = dep.destination === "other";
        const isOwner = dep.destination === "owner";
        const isMomo = dep.destination === "momo";
        const withRef = needsRef(dep.destination);
        const ready = !!actionShop && amount > 0 && !tooMuch && (!isOther || !!dep.reason.trim());
        const accounts = (accountsQ.data?.accounts ?? []).filter((a) => a.kind === dep.destination);
        const accountLabel = isMomo ? "MoMo number" : "Bank account";
        const icons: Record<string, string> = { bank: "bank", momo: "phone", owner: "person", other: "dots" };
        const summary = amount > 0 ? `${money(amount)} to ${isOwner ? "the owner" : isOther ? "somewhere else" : isMomo ? "Mobile Money" : "the bank"}` : "Enter an amount";
        return (
          <Modal wide title="Transfer cash" sub="Move cash out of the safe and keep a record of where it went." onClose={() => setDep(null)} footer={<div className="flex w-full flex-wrap items-center justify-between gap-3"><span className="text-sm font-semibold">{summary}</span><span className="flex gap-2"><Button onClick={() => setDep(null)}>Cancel</Button><Button tone="primary" disabled={!ready} onClick={() => void deposit()}>Save transfer</Button></span></div>}>
            <div className="grid gap-5 md:grid-cols-2">
              <div className="flex min-w-0 flex-col gap-4">
                {allShops ? <Field label="From which shop?"><Select value={targetShop} onChange={(e) => setTargetShop(e.target.value)}><option value="">Choose a shop</option>{shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field> : null}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="transfer-amount" className="text-[13px] font-semibold">How much?</label>
                  <div className={cx("flex h-14 items-center gap-2 rounded-xl border bg-white px-3.5 focus-within:border-brand", tooMuch ? "border-stop" : "border-line-2")}>
                    <span className="text-xl font-bold text-slate-500">₵</span>
                    <input id="transfer-amount" inputMode="decimal" value={dep.amount} onChange={(e) => setDep({ ...dep, amount: e.target.value })} placeholder="0.00" className="h-full min-w-0 flex-1 bg-transparent text-2xl font-extrabold tracking-tight outline-none focus:!shadow-none" />
                  </div>
                  {holds != null ? (
                    <span className="flex items-center justify-between gap-2 text-[13px]">
                      {tooMuch ? <span className="font-semibold text-stop">More than this safe holds ({money(holds)})</span> : <span className="text-slate-500">In this safe: {money(holds)}</span>}
                      {holds > 0 ? <button type="button" className="min-h-8 font-bold text-brand hover:underline" onClick={() => setDep({ ...dep, amount: fromMinor(holds) })}>Use all</button> : null}
                    </span>
                  ) : <span className="text-[13px] text-slate-500">Choose a shop to see what its safe holds</span>}
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-[13px] font-semibold">Where is it going?</span>
                  <div role="radiogroup" aria-label="Where is it going?" className="grid grid-cols-2 gap-2">
                    {Object.entries(DEST).map(([k, l]) => (
                      <button key={k} type="button" role="radio" aria-checked={dep.destination === k} onClick={() => setDep({ ...dep, destination: k, account: "", noRef: false, reason: k === "other" ? "" : k === "momo" ? "Message not received yet" : "Slip book finished" })} className={cx("flex min-h-14 items-center gap-2.5 rounded-xl border-[1.5px] px-3 text-left text-[13px] font-bold transition-colors", dep.destination === k ? "border-brand bg-brand-tint text-brand-ink" : "border-slate-200 bg-white hover:border-slate-400")}>
                        <Icon name={icons[k]!} size={18} className="flex-none" />{l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex min-w-0 flex-col gap-4 rounded-2xl bg-slate-50 p-4">
                <b className="text-sm">{isOwner ? "Who received it" : isOther ? "What it was for" : isMomo ? "Mobile Money details" : "Bank details"}</b>
                {withRef ? (
                  accounts.length ? (
                    <Field label={accountLabel}><Select value={dep.account} onChange={(e) => setDep({ ...dep, account: e.target.value })}><option value="">Choose one</option>{accounts.map((a) => <option key={a.name} value={a.name}>{a.name}</option>)}</Select></Field>
                  ) : (
                    <Field label={accountLabel} hint={<>Save yours in <Link to="/settings/payments" className="font-semibold text-brand underline">Settings</Link> to pick from a list</>}><Input value={dep.account} onChange={(e) => setDep({ ...dep, account: e.target.value })} maxLength={60} /></Field>
                  )
                ) : null}
                {isOther ? <Field label="What was it for?"><Input value={dep.reason} onChange={(e) => setDep({ ...dep, reason: e.target.value })} maxLength={100} placeholder="e.g. Paid the transport company" /></Field> : null}
                <Field label={isOwner ? "Name" : "Who is taking it?"} hint={isOwner ? undefined : "Leave blank if it's you"}><Input value={dep.taker} onChange={(e) => setDep({ ...dep, taker: e.target.value })} /></Field>
                {withRef ? (
                  <>
                    {dep.noRef ? (
                      <Field label="Why is there no number yet?"><Select value={dep.reason} onChange={(e) => setDep({ ...dep, reason: e.target.value })}>{isMomo ? <><option>Message not received yet</option><option>With the person who sent it</option><option>Other</option></> : <><option>Slip book finished</option><option>Slip with the person who went to the bank</option><option>Other</option></>}</Select></Field>
                    ) : (
                      <Field label={isMomo ? "Transaction ID" : "Slip number"}><Input value={dep.slip} onChange={(e) => setDep({ ...dep, slip: e.target.value })} /></Field>
                    )}
                    <button type="button" className="-mt-2 min-h-8 self-start text-[13px] font-bold text-brand hover:underline" onClick={() => setDep({ ...dep, noRef: !dep.noRef, slip: "" })}>{dep.noRef ? `I have the ${isMomo ? "transaction ID" : "slip number"}` : "I don't have it yet"}</button>
                    <label className="flex min-h-12 cursor-pointer items-center gap-3 rounded-xl border border-dashed border-slate-300 bg-white px-3.5 py-2 text-sm hover:border-brand focus-within:border-brand">
                      <Icon name="label" size={17} className="flex-none text-slate-500" />
                      <span className="min-w-0 flex-1 font-semibold">{dep.slipPhoto ? "Photo added" : "Add a photo (optional)"}</span>
                      <span className="flex-none text-[13px] font-bold text-brand">{dep.slipPhoto ? "Change" : "Choose"}</span>
                      <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => void uploadSlip(e.target.files?.[0])} className="sr-only" />
                    </label>
                  </>
                ) : null}
              </div>
            </div>
          </Modal>
        );
      })() : null}
      {act ? (
        <Modal title={act.action === "reopen" ? "Undo: not checked yet" : act.action === "correct" ? "Correct the amount" : act.hasSlip ? "Change the reference" : "Add the reference"} onClose={() => setAct(null)} footer={<><Button onClick={() => setAct(null)}>Cancel</Button><Button tone="primary" onClick={() => void run(act.id, act.action === "correct" ? { action: "correct", amount: toMinor(act.value), reason: act.reason } : act.action === "slip" ? { action: "slip", slip: act.value, reason: act.reason } : { action: "reopen", reason: act.reason })}>Save</Button></>}>
          <div className="flex flex-col gap-3">
            {act.action !== "reopen" ? <Field label={act.action === "correct" ? "Correct amount" : "Slip or transaction number"}><Input value={act.value} onChange={(e) => setAct({ ...act, value: e.target.value })} /></Field> : null}
            {act.action !== "slip" || act.hasSlip ? <Field label="Reason (required)"><Input value={act.reason} onChange={(e) => setAct({ ...act, reason: e.target.value })} /></Field> : null}
          </div>
        </Modal>
      ) : null}
      {float ? (
        <Modal title="Take starting cash from the safe" onClose={() => setFloat(null)} footer={<><Button onClick={() => setFloat(null)}>Cancel</Button><Button tone="primary" disabled={!actionShop} onClick={() => void saveFloat()}>Save</Button></>}>
          <div className="flex flex-col gap-3">
            {allShops ? <Field label="Which shop"><Select value={targetShop} onChange={(e) => setTargetShop(e.target.value)}><option value="">Choose a shop</option>{shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field> : null}
            <Field label="Amount"><Input inputMode="decimal" value={float.amount} onChange={(e) => setFloat({ ...float, amount: e.target.value })} /></Field>
            <Field label="For which till"><Input value={float.till} onChange={(e) => setFloat({ ...float, till: e.target.value })} /></Field>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

// ─────────────────────────── roles and permissions ───────────────────────────
const AREA_GROUPS: { label: string; areas: [string, string][] }[] = [
  { label: "Products and stock", areas: [["products", "Add and edit products"], ["stock", "Receive and count stock"], ["discounts", "Set discounts"]] },
  { label: "Money", areas: [["cash", "Count cash and transfer it"], ["confirmDeposits", "Check cash transfers arrived"], ["refunds", "Approve refunds cashiers start at the till"], ["money", "Take customer payments and manage money owed"]] },
  { label: "People and setup", areas: [["staff", "Manage till staff"], ["team", "Manage the team and these permissions"], ["settings", "Change settings, shops and tills"]] },
];

export function Permissions() {
  const toast = useToast();
  const limitsQ = useQuery({ queryKey: ["admin-settings"], queryFn: () => api<{ settings: { discountLimit: Record<"cashier" | "supervisor" | "manager", number>; refundLimit: Record<"cashier" | "supervisor" | "manager", number | null> } }>(till("/admin/settings")), enabled: !!import.meta.env.VITE_BRAND });
  const q = useQuery({ queryKey: ["permissions"], queryFn: () => api<{ areas: Record<string, string[]>; shops: Record<string, string>; roles: { id: string; label: string; custom: boolean }[] }>(till("/admin/permissions")) });
  const shopsQ = useQuery({ queryKey: ["shops"], queryFn: () => api<{ shops: Shop[] }>(till("/admin/shops")) });
  const [areas, setAreas] = useState<Record<string, string[]> | null>(null);
  const [custom, setCustom] = useState<{ id: string; label: string }[] | null>(null);
  const [shopsOf, setShopsOf] = useState<Record<string, string> | null>(null);
  const [newRole, setNewRole] = useState("");
  const cur = areas ?? q.data?.areas ?? {};
  const curCustom = custom ?? (q.data?.roles ?? []).filter((r) => r.custom).map((r) => ({ id: r.id, label: r.label }));
  const curShops = shopsOf ?? q.data?.shops ?? {};
  const roles = [...(q.data?.roles ?? []).filter((r) => !r.custom), ...curCustom];
  const dirty = !!areas || !!custom || !!shopsOf;
  const teamQ = useQuery({ queryKey: ["team"], queryFn: () => api<{ members: { role: string }[]; invites: { role: string }[] }>(office("/team")) });
  const peopleIn = (id: string) => (teamQ.data?.members ?? []).filter((m) => m.role === id).length;
  const discard = () => {
    setAreas(null);
    setCustom(null);
    setShopsOf(null);
  };
  const addRole = () => {
    const label = newRole.trim().replace(/\s+/g, " ");
    if (!label) return;
    if (roles.some((r) => r.label.toLowerCase() === label.toLowerCase())) return toast("A role already has this name", "err");
    setCustom([...curCustom, { id: `role-${crypto.randomUUID().slice(0, 8)}`, label }]);
    setNewRole("");
  };
  // A role can only go once nobody has it, so nobody is left without one.
  const confirmBox = useConfirm();
  const removeRole = async (r: { id: string; label: string }) => {
    try {
      const team = await api<{ members: { role: string }[]; invites: { role: string }[] }>(office("/team"));
      const using = team.members.filter((m) => m.role === r.id).length + team.invites.filter((i) => i.role === r.id).length;
      if (using) return toast(`${using} ${using === 1 ? "person has" : "people have"} the ${r.label} role. Change them on the Team page first`, "err");
      if (!(await confirmBox({ title: `Remove the ${r.label} role?`, body: "This can't be undone, but you can add the role again.", ok: "Remove role", danger: true }))) return;
      const nextCustom = curCustom.filter((x) => x.id !== r.id);
      const nextShops = { ...curShops };
      delete nextShops[r.id];
      const nextAreas = Object.fromEntries(Object.entries(cur).map(([a, list]) => [a, list.filter((x) => x !== r.id)]));
      await api(till("/admin/permissions"), { method: "PUT", body: { areas: nextAreas, custom: nextCustom, shops: nextShops } });
      setAreas(null);
      setCustom(null);
      setShopsOf(null);
      await q.refetch();
      toast(`${r.label} removed`);
    } catch (e) {
      toast(errText(e, "Couldn't remove the role"), "err");
    }
  };
  const flip = (area: string, role: string, on: boolean) => setAreas({ ...cur, [area]: on ? [...(cur[area] ?? []), role] : (cur[area] ?? []).filter((r) => r !== role) });
  const save = async () => {
    try {
      await api(till("/admin/permissions"), { method: "PUT", body: { areas: cur, custom: curCustom, shops: curShops } });
      toast("Permissions saved");
      setAreas(null);
      setCustom(null);
      setShopsOf(null);
      void q.refetch();
    } catch (e) {
      toast(errText(e, "Couldn't save"), "err");
    }
  };
  const sticky = "sticky left-0 z-10 bg-white";
  return (
    <>
      <PageHead title="Roles & permissions" sub={import.meta.env.VITE_BRAND ? "Back office roles (password) are set below. Till roles (PIN) are further down." : "Choose what each back office role can do. The owner can always do everything."}>
        <form onSubmit={(e) => { e.preventDefault(); addRole(); }} className="flex flex-wrap gap-2">
          <Input aria-label="New role name" value={newRole} onChange={(e) => setNewRole(e.target.value)} placeholder="New role name" className="!w-52" />
          <Button type="submit" disabled={!newRole.trim()}>Add role</Button>
        </form>
      </PageHead>
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-sm">
            <thead>
              <tr>
                <th className={cx(sticky, "min-w-[250px] border-b border-slate-100 px-4 py-3 text-left text-xs font-semibold text-slate-500")}>What they can do</th>
                {roles.map((r) => (
                  <th key={r.id} className="min-w-[112px] border-b border-slate-100 px-2 py-3 text-center align-top font-normal">
                    <b className="block text-[13px] font-bold text-ink">{r.label}</b>
                    <span className="block text-xs text-slate-500">{peopleIn(r.id)} {peopleIn(r.id) === 1 ? "person" : "people"}</span>
                    {curCustom.some((x) => x.id === r.id) ? <button type="button" aria-label={`Remove the ${r.label} role`} onClick={() => void removeRole(r)} className="mt-0.5 min-h-7 text-xs font-semibold text-red-700 hover:underline">Remove</button> : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shopsQ.data?.shops.length ? (
                <tr>
                  <td className={cx(sticky, "border-b border-slate-100 px-4 py-2.5 font-semibold")}>Which shops<span className="block text-xs font-normal text-slate-500">What this role can see and change</span></td>
                  {roles.map((r) => (
                    <td key={r.id} className="border-b border-slate-100 px-2 py-2 text-center">
                      {r.id === "owner" ? <span className="text-xs font-semibold text-slate-500">All shops</span> : (
                        <Select aria-label={`Shop for ${r.label}`} value={curShops[r.id] ?? ""} onChange={(e) => { const next = { ...curShops }; if (e.target.value) next[r.id] = e.target.value; else delete next[r.id]; setShopsOf(next); }} className="!h-10 !w-[124px] !px-2 !text-xs">
                          <option value="">All shops</option>
                          {shopsQ.data?.shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </Select>
                      )}
                    </td>
                  ))}
                </tr>
              ) : null}
              {AREA_GROUPS.map((g) => (
                <Fragment key={g.label}>
                  <tr><td colSpan={roles.length + 1} className="bg-slate-50 px-4 py-2 text-xs font-bold text-slate-600">{g.label}</td></tr>
                  {g.areas.map(([a, l]) => (
                    <tr key={a} className="hover:bg-slate-50/60">
                      <td className={cx(sticky, "border-b border-slate-100 px-4 py-2.5 font-medium")}>{l}</td>
                      {roles.map((r) => {
                        const on = r.id === "owner" || (cur[a] ?? []).includes(r.id);
                        return (
                          <td key={r.id} className="border-b border-slate-100 px-2 py-2 text-center">
                            <button type="button" role="switch" aria-checked={on} aria-label={`${r.label}: ${l}`} disabled={r.id === "owner"} onClick={() => flip(a, r.id, !on)} className={cx("relative inline-block h-6 w-10 !rounded-full align-middle transition-colors disabled:cursor-not-allowed disabled:opacity-55", on ? "bg-brand" : "bg-slate-300")}>
                              <span className={cx("absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all", on ? "left-[18px]" : "left-0.5")} />
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <p className="px-4 py-3 text-xs text-slate-500">Everyone with back office access can see dashboards, lists and reports. These switches control what they can change.</p>
      </Card>
      {import.meta.env.VITE_BRAND ? <div className="mt-5"><h2 className="mb-2 text-base font-extrabold">Till roles</h2><p className="mb-3 text-[13px] text-slate-500">For staff who sign in at the till with a PIN. Everyone sees the same till screens; the role decides what they can approve without someone else's PIN.</p><div className="flex flex-col gap-4"><YourTillRoles limits={limitsQ.data?.settings ? { discount: limitsQ.data.settings.discountLimit, refund: limitsQ.data.settings.refundLimit } : undefined} /><TillRoles open /></div></div> : null}
      {dirty ? (
        <div role="status" className="sticky bottom-4 z-20 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-[14px] bg-ink px-4 py-3 text-white shadow-lg">
          <b className="text-sm">You have unsaved changes</b>
          <span className="flex gap-2"><button type="button" onClick={discard} className="h-10 px-3 text-sm font-semibold text-white/80 hover:text-white">Discard</button><Button tone="primary" onClick={() => void save()}>Save changes</Button></span>
        </div>
      ) : null}
    </>
  );
}
